/**
 * Utility for executing async operations with controlled concurrency
 */

export interface ParallelExecutionOptions<T> {
  concurrency: number;
  onProgress?: (completed: number, total: number) => void;
  onError?: (item: T, error: Error) => void;
}

export interface ParallelExecutionResult<T, R> {
  item: T;
  result?: R;
  error?: Error;
}

/**
 * Execute async operations with controlled concurrency
 */
export async function executeWithConcurrency<T, R>(
  items: T[],
  asyncFn: (item: T) => Promise<R>,
  options: ParallelExecutionOptions<T>
): Promise<Array<ParallelExecutionResult<T, R>>> {
  const { concurrency, onProgress, onError } = options;
  const results: Array<ParallelExecutionResult<T, R>> = [];
  let completed = 0;
  const total = items.length;

  // Process items in batches
  for (let i = 0; i < items.length; i += concurrency) {
    const batch = items.slice(i, i + concurrency);

    const batchResults = await Promise.allSettled(
      batch.map(async (item) => {
        try {
          const result = await asyncFn(item);
          return { item, result };
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          onError?.(item, err);
          return { item, error: err };
        }
      })
    );

    // Process batch results
    batchResults.forEach((settled, index) => {
      const item = batch[index];

      if (settled.status === 'fulfilled') {
        results.push(settled.value);
      } else {
        // This shouldn't happen since we catch errors in the map, but handle it anyway
        const error = settled.reason instanceof Error ? settled.reason : new Error(String(settled.reason));
        results.push({ item, error });
        onError?.(item, error);
      }
      completed++;
      onProgress?.(completed, total);
    });
  }

  return results;
}
